// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  input,
  ViewChild,
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { ChartsService } from '../services/charts-data.service';

@Component({
  selector: 'app-highest-consumed-graph',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  standalone: true,
  imports: [],
  templateUrl: './highest-consumed-graph.component.html',
  styleUrl: './highest-consumed-graph.component.scss',
})
export class HighestConsumedGraphComponent implements AfterViewInit {
  values = input<any[]>([]);

  @ViewChild('chart') chartViewRef: ElementRef<HTMLDivElement> | undefined;

  private readonly defaultColor = '#7D7D7D';
  private readonly highlightColor = '#EC6602';
  private readonly colorLabel = 'rgba(0, 0, 0, 0.90)';
  private readonly siemensSans = 'siemens sans';

  public constructor(
    private readonly translateService: TranslateService,
    private readonly chartService: ChartsService,
  ) {}

  private readonly graphLabels = {
    title: this.translateService.instant('spareParts.HighestConsumed.title'),
    subtitle: this.translateService.instant('spareParts.subtitle.last12Months'),
  };

  public ngAfterViewInit(): void {
    const chartEl = this.chartViewRef?.nativeElement;
    const option = this.chartService.buildDefaultOptions(
      this.chartService.buildTitle(
        this.graphLabels.title,
        this.graphLabels.subtitle,
      ),
      this.buildXAxis(),
      this.buildYAxis(),
      this.buildSeries(),
      {
        right: 0,
        left: 300,
        top: 50,
        bottom: 0,
      },
    );
    if (chartEl) {
      this.chartService.buildChart(chartEl, option);
    }
  }

  protected buildXAxis(): { [key: string]: any } {
    return {
      type: 'value',
      minInterval: 1,
      splitLine: {
        show: false,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
    };
  }

  protected buildYAxis(): { [key: string]: any } {
    const parts: any = this.values();
    return {
      position: 'left',
      type: 'category',
      inverse: true,
      data: parts.map((item: any) => item.partDescription),
      axisLabel: {
        formatter: (value: any, index: number) => {
          const sub = parts[index].partNumber;
          return `{main|${value}} ID {sub|${sub}}`;
        },

        rich: {
          main: {
            fontFamily: this.siemensSans,
            fontWeight: 400,
            fontSize: 14,
            color: this.colorLabel,
            align: 'left',
          },
          sub: {
            fontFamily: this.siemensSans,
            fontWeight: 400,
            fontSize: 14,
            color: this.defaultColor,
            align: 'left',
          },
        },
        align: 'right',
        width: 270,
        overflow: 'truncate',
      },
      axisLine: { show: false },
      axisTick: { show: false },
    };
  }

  protected buildSeries(): { [key: string]: any } {
    const parts = this.values();

    const values = parts.map((item: any) => item.totalConsumptionCount);

    const maxValue = Math.max(...values);

    const data = parts.map((v: any) =>
      v.totalConsumptionCount === maxValue
        ? {
            value: v.totalConsumptionCount,
            itemStyle: { color: this.highlightColor },
          }
        : {
            value: v.totalConsumptionCount,
            itemStyle: { color: this.defaultColor },
          },
    );
    return [
      {
        type: 'bar',
        data: data,
        showBackground: true,
        label: {
          show: true,
          position: 'inside',
          color: 'white',
          fontWeight: 'bold',
        },
      },
    ];
  }
}
